import React from 'react';
import cl from './Diagram.module.css';

/**
 * Diagram — СТАТИЧНАЯ схема для кадров-объяснений.
 *
 * Почему статика, а не анимация: метаанализ Höffler & Leutner показывает,
 * что анимация даёт большой эффект (d ≈ 1.06) только для ПРОЦЕДУРНОГО
 * знания — действий. Для объяснения КОНЦЕПЦИЙ эффект мал (d ≈ 0.37),
 * а транзиторность (информация исчезает с экрана) добавляет лишнюю
 * нагрузку на память. Статичную схему можно рассматривать сколько нужно.
 *
 * Подписи ставятся РЯДОМ с элементом, а не в легенде внизу — принцип
 * пространственной смежности (d = 0.82).
 *
 * Схемы переписаны под «Кубикон CAD»: устройство окна нашего редактора,
 * оси с Z вверх (в CAD высота идёт по Z, а не по Y как в игровых движках),
 * переход «эскиз → объём» и чтение габаритов.
 *
 * Props:
 *   kind   — какая схема
 *   labels — [{ at: 'ribbon', text: 'инструменты' }]
 */

/*
 * Перенос текста по словам для SVG.
 *
 * В SVG нет автопереноса: длинная строка просто вылезает за любую рамку.
 * Раньше это лечили подбором ширины и кегля, но текст в схемы приходит
 * из уроков, и его длина заранее неизвестна — следующий урок передаст
 * строку длиннее, и всё повторится.
 *
 * charW — средняя ширина символа относительно кегля. Значения ИЗМЕРЕНЫ
 * в браузере через getComputedTextLength, а не взяты на глаз:
 * Roboto Condensed — 0.526, моноширинный — 0.550. Здесь стоят с запасом,
 * потому что кириллица шире латиницы, на которой обычно меряют.
 */
function wrapText(text, maxWidth, fontSize, charW = 0.56) {
    const limit = Math.max(1, Math.floor(maxWidth / (fontSize * charW)));
    const words = String(text || '').split(' ');
    const lines = [];
    let cur = '';
    for (const w of words) {
        const next = cur ? cur + ' ' + w : w;
        if (next.length <= limit) { cur = next; continue; }
        if (cur) lines.push(cur);
        cur = w;
    }
    if (cur) lines.push(cur);
    return lines.length ? lines : [''];
}

/** Многострочный текст: строки идут вниз от базовой линии по центру блока. */
function MultiText({ x, y, lines, fontSize, className, style, lineGap = 1.25 }) {
    const h = fontSize * lineGap;
    const top = y - ((lines.length - 1) * h) / 2;
    return (
        <>
            {lines.map((ln, i) => (
                <text key={i} x={x} y={top + i * h} className={className}
                    style={style}>{ln}</text>
            ))}
        </>
    );
}

export default function Diagram({ kind, labels = [] }) {
    const byAt = Object.fromEntries(labels.map((l) => [l.at, l.text]));

    switch (kind) {
        case 'layout':      return <LayoutDiagram labels={byAt} />;
        case 'axes':        return <AxesDiagram labels={byAt} />;
        case 'sketch-solid': return <SketchToSolid labels={byAt} />;
        case 'dims':        return <DimsDiagram labels={byAt} />;
        case 'planes':      return <PlanesDiagram labels={byAt} />;
        /* Курс 2 «Читаем чертежи» */
        case 'views':       return <ViewsDiagram labels={byAt} />;
        case 'hidden':      return <HiddenLinesDiagram labels={byAt} />;
        case 'section':     return <SectionDiagram labels={byAt} />;
        /* Курс 3 «Детали, которые собираются» */
        case 'gap':         return <GapDiagram labels={byAt} />;
        case 'fits':        return <FitsDiagram labels={byAt} />;
        case 'snap':        return <SnapDiagram labels={byAt} />;
        case 'printable':   return <PrintableDiagram labels={byAt} />;
        /* Курс 5 «Проектирование по заданию» */
        case 'brief':       return <BriefDiagram labels={byAt} />;
        case 'measure':     return <MeasureDiagram labels={byAt} />;
        case 'lighten':     return <LightenDiagram labels={byAt} />;
        case 'split':       return <SplitDiagram labels={byAt} />;
        default:            return <div className={cl.wrap} />;
    }
}


/** Устройство окна редактора: где что находится. */
function LayoutDiagram({ labels }) {
    return (
        <div className={cl.wrap}>
            <div className={cl.mock}>
                <div className={`${cl.zone} ${cl.zRibbon}`}>
                    <span className={cl.zoneName}>Лента инструментов</span>
                    {labels.ribbon && <span className={cl.tag}>{labels.ribbon}</span>}
                </div>
                <div className={cl.mockBody}>
                    <div className={`${cl.zone} ${cl.zLeft}`}>
                        <span className={cl.zoneName}>Браузер</span>
                        {labels.left && <span className={cl.tag}>{labels.left}</span>}
                    </div>
                    <div className={`${cl.zone} ${cl.zScene}`}>
                        <span className={cl.zoneName}>Сцена</span>
                        {labels.scene && <span className={cl.tag}>{labels.scene}</span>}
                    </div>
                    <div className={`${cl.zone} ${cl.zRight}`}>
                        <span className={cl.zoneName}>Свойства</span>
                        {labels.right && <span className={cl.tag}>{labels.right}</span>}
                    </div>
                </div>
            </div>
        </div>
    );
}


/**
 * Три оси. ⚠️ В CAD высота идёт по Z — это отличается от игровых движков,
 * где вверх смотрит Y. Путаница здесь стоит ученику дорого, поэтому
 * ось Z выделена и подписана «высота».
 */
function AxesDiagram({ labels }) {
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                <defs>
                    <marker id="dgArrow" markerWidth="7" markerHeight="7"
                        refX="6" refY="3.5" orient="auto">
                        <path d="M0 0 L7 3.5 L0 7 Z" fill="currentColor" />
                    </marker>
                </defs>

                <g strokeWidth="3" fill="none" markerEnd="url(#dgArrow)">
                    <path d="M160 140 L160 40" stroke="#4a7ec0" color="#4a7ec0" />
                    <path d="M160 140 L272 178" stroke="#c0504d" color="#c0504d" />
                    <path d="M160 140 L48 178" stroke="#4f9a4f" color="#4f9a4f" />
                </g>
                <circle cx="160" cy="140" r="4" fill="#23252b" />

                <text x="160" y="30" className={cl.axName} fill="#4a7ec0">Z</text>
                <text x="290" y="186" className={cl.axName} fill="#c0504d">X</text>
                <text x="30" y="186" className={cl.axName} fill="#4f9a4f">Y</text>

                {/* Подписи РЯДОМ с осью, а не в легенде внизу. */}
                <text x="160" y="16" className={cl.axHint} textAnchor="middle">
                    {labels.z || 'высота'}
                </text>
                <text x="278" y="162" className={cl.axHint} textAnchor="end">
                    {labels.x || 'вправо'}
                </text>
                <text x="42" y="162" className={cl.axHint} textAnchor="start">
                    {labels.y || 'вглубь'}
                </text>
            </svg>
        </div>
    );
}


/** Эскиз → объём: главная мысль всего курса. */
function SketchToSolid({ labels }) {
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                {/* Слева — плоский эскиз */}
                <g>
                    <polygon points="20,132 84,110 132,130 68,152"
                        fill="rgba(47,134,214,.10)" stroke="#2f86d6"
                        strokeWidth="2.4" strokeDasharray="6 4"/>
                    <text x="76" y="176" className={cl.axHint} textAnchor="middle">
                        {labels.sketch || 'эскиз — плоская фигура'}
                    </text>
                </g>

                {/* Стрелка перехода */}
                <path d="M146 132 L178 132" stroke="#5a6474" strokeWidth="2.5"
                    fill="none" markerEnd="url(#dgArrow2)"/>
                <defs>
                    <marker id="dgArrow2" markerWidth="7" markerHeight="7"
                        refX="6" refY="3.5" orient="auto">
                        <path d="M0 0 L7 3.5 L0 7 Z" fill="#5a6474" />
                    </marker>
                </defs>

                {/* Справа — объёмное тело из того же эскиза */}
                <g>
                    <polygon points="196,96 260,74 308,94 244,116"
                        fill="#b8c2d0" stroke="#5b6472" strokeWidth="1.6"/>
                    <polygon points="196,96 244,116 244,158 196,138"
                        fill="#8d97a7" stroke="#5b6472" strokeWidth="1.6"/>
                    <polygon points="244,116 308,94 308,136 244,158"
                        fill="#a2acbb" stroke="#5b6472" strokeWidth="1.6"/>
                    <text x="252" y="182" className={cl.axHint} textAnchor="middle">
                        {labels.solid || 'выдавили — стало телом'}
                    </text>
                </g>
            </svg>
        </div>
    );
}


/** Габариты детали: три числа, которые её описывают. */
function DimsDiagram({ labels }) {
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                {/* Деталь */}
                <polygon points="80,74 196,54 268,80 152,102"
                    fill="#b8c2d0" stroke="#5b6472" strokeWidth="1.6"/>
                <polygon points="80,74 152,102 152,150 80,122"
                    fill="#8d97a7" stroke="#5b6472" strokeWidth="1.6"/>
                <polygon points="152,102 268,80 268,128 152,150"
                    fill="#a2acbb" stroke="#5b6472" strokeWidth="1.6"/>

                {/* Размерные линии — рядом с той стороной, которую меряют */}
                <g stroke="#2f86d6" strokeWidth="1.6" fill="none">
                    <path d="M152 160 L268 138"/>
                    <path d="M64 76 L64 124"/>
                    <path d="M74 62 L190 42"/>
                </g>
                <text x="212" y="166" className={cl.dimTxt} textAnchor="middle">
                    {labels.w || '40'}
                </text>
                <text x="48" y="104" className={cl.dimTxt} textAnchor="middle">
                    {labels.h || '20'}
                </text>
                <text x="128" y="42" className={cl.dimTxt} textAnchor="middle">
                    {labels.d || '30'}
                </text>

                <text x="160" y="192" className={cl.axHint} textAnchor="middle">
                    {labels.note || 'три числа описывают деталь целиком'}
                </text>
            </svg>
        </div>
    );
}


/** Три плоскости, на которых можно начать эскиз. */
function PlanesDiagram({ labels }) {
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                {/* XY — горизонтальная */}
                <polygon points="80,124 176,98 264,124 168,150"
                    fill="rgba(47,134,214,.16)" stroke="#2f86d6" strokeWidth="2"/>
                {/* XY подписываем ПОД плоскостью: справа надпись ложилась на её контур. */}
                <text x="160" y="166" className={cl.axHint}
                    style={{ fontSize: '11.5px' }}>{labels.xy || 'XY — сверху'}</text>

                {/* XZ — вертикальная, вдоль X */}
                <polygon points="80,124 176,98 176,44 80,70"
                    fill="rgba(192,80,77,.12)" stroke="#c0504d" strokeWidth="2"/>
                <text x="104" y="32" className={cl.axHint}
                    style={{ fontSize: '11.5px' }}>{labels.xz || 'XZ — спереди'}</text>

                {/* YZ — вертикальная, вдоль Y */}
                <polygon points="176,98 264,124 264,70 176,44"
                    fill="rgba(79,154,79,.12)" stroke="#4f9a4f" strokeWidth="2"/>
                <text x="236" y="32" className={cl.axHint}
                    style={{ fontSize: '11.5px' }}>{labels.yz || 'YZ — сбоку'}</text>

                <text x="160" y="186" className={cl.axHint} textAnchor="middle">
                    {labels.note || 'эскиз рисуют на одной из них'}
                </text>
            </svg>
        </div>
    );
}


/*
 * ── КУРС 2. «Читаем чертежи» ──────────────────────────────────────────
 */

/**
 * Откуда берутся проекции: на деталь смотрят с трёх сторон, и каждый
 * взгляд даёт свой плоский вид.
 *
 * Ключ к пониманию — стрелки взгляда. Без них ребёнок видит три
 * прямоугольника и не понимает, почему они разные.
 */
function ViewsDiagram({ labels }) {
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                <defs>
                    <marker id="dgEye" markerWidth="7" markerHeight="7"
                        refX="6" refY="3.5" orient="auto">
                        <path d="M0 0 L7 3.5 L0 7 Z" fill="#2f86d6" />
                    </marker>
                </defs>

                {/* Деталь в объёме — слева */}
                <polygon points="34,96 84,80 124,98 74,114"
                    fill="#b8c2d0" stroke="#5b6472" strokeWidth="1.5"/>
                <polygon points="34,96 74,114 74,150 34,132"
                    fill="#8d97a7" stroke="#5b6472" strokeWidth="1.5"/>
                <polygon points="74,114 124,98 124,134 74,150"
                    fill="#a2acbb" stroke="#5b6472" strokeWidth="1.5"/>

                {/* Три взгляда — стрелки к своим видам */}
                <g stroke="#2f86d6" strokeWidth="1.4" fill="none"
                    strokeDasharray="4 3" markerEnd="url(#dgEye)">
                    <path d="M132 106 L196 48"/>
                    <path d="M132 116 L196 104"/>
                    <path d="M112 156 L196 160"/>
                </g>

                {/* Виды: у каждого своя пропорция — это и есть суть */}
                <g fill="#ffffff" stroke="#23252b" strokeWidth="1.8">
                    <rect x="202" y="34" width="56" height="28"/>
                    <rect x="202" y="90" width="34" height="28"/>
                    <rect x="202" y="146" width="56" height="30"/>
                </g>

                {/* Подписи ПОД рамками, а не сбоку: у .axHint в CSS задан
                    text-anchor: middle, он перебивает атрибут из JSX, и
                    боковая подпись центрируется по своей точке, заезжая
                    на рамку. Под рамкой конфликта нет вовсе. */}
                <text x="230" y="84" className={cl.axHint}
                    style={{ fontSize: '11px' }}>
                    {labels.front || 'спереди'}
                </text>
                <text x="219" y="138" className={cl.axHint}
                    style={{ fontSize: '11px' }}>
                    {labels.side || 'слева'}
                </text>
                <text x="230" y="196" className={cl.axHint}
                    style={{ fontSize: '11px' }}>
                    {labels.top || 'сверху'}
                </text>

                <text x="76" y="186" className={cl.axHint}
                    style={{ fontSize: '11.5px' }}>
                    {labels.note || 'один предмет — три вида'}
                </text>
            </svg>
        </div>
    );
}


/**
 * Невидимые линии: то, что скрыто внутри детали, на чертеже рисуют
 * штриховой линией.
 */
function HiddenLinesDiagram({ labels }) {
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                {/* Деталь в объёме с отверстием — слева */}
                <polygon points="26,80 82,62 132,82 76,100"
                    fill="#b8c2d0" stroke="#5b6472" strokeWidth="1.5"/>
                <polygon points="26,80 76,100 76,140 26,120"
                    fill="#8d97a7" stroke="#5b6472" strokeWidth="1.5"/>
                <polygon points="76,100 132,82 132,122 76,140"
                    fill="#a2acbb" stroke="#5b6472" strokeWidth="1.5"/>
                <ellipse cx="79" cy="81" rx="11" ry="5" fill="#1a130c"/>
                <text x="79" y="160" className={cl.axHint} textAnchor="middle">
                    {labels.solid || 'отверстие видно сверху'}
                </text>

                {/* Тот же предмет видом спереди — отверстие СКРЫТО */}
                <rect x="182" y="74" width="104" height="46"
                    fill="#ffffff" stroke="#23252b" strokeWidth="2"/>
                <g stroke="#8a93a3" strokeWidth="1.3" strokeDasharray="6 4">
                    <path d="M212 74 L212 120"/>
                    <path d="M240 74 L240 120"/>
                </g>
                <text x="234" y="138" className={cl.axHint} textAnchor="middle">
                    {labels.hidden || 'спереди — штриховой линией'}
                </text>

                <text x="160" y="186" className={cl.axHint} textAnchor="middle">
                    {labels.note || 'штриховая линия = то, что внутри'}
                </text>
            </svg>
        </div>
    );
}


/**
 * Разрез: деталь мысленно разрезают, чтобы показать внутренность.
 * Срез штрихуют под 45°.
 */
function SectionDiagram({ labels }) {
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                <defs>
                    {/* Штриховка среза — по ГОСТ под 45° */}
                    <pattern id="dgHatch" width="7" height="7"
                        patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                        <line x1="0" y1="0" x2="0" y2="7"
                            stroke="#5b6472" strokeWidth="1.1"/>
                    </pattern>
                </defs>

                {/* Слева — целая деталь с линией разреза */}
                <rect x="26" y="72" width="100" height="56"
                    fill="#ffffff" stroke="#23252b" strokeWidth="2"/>
                <g stroke="#8a93a3" strokeWidth="1.3" strokeDasharray="6 4">
                    <path d="M58 72 L58 128"/>
                    <path d="M94 72 L94 128"/>
                </g>
                {/* Линия, по которой режем — штрихпунктир со стрелками */}
                <path d="M14 100 L138 100" stroke="#c0504d" strokeWidth="1.6"
                    strokeDasharray="10 3 2 3" fill="none"/>
                <text x="76" y="152" className={cl.axHint} textAnchor="middle">
                    {labels.whole || 'режем вот здесь'}
                </text>

                {/* Справа — разрез: внутренность видна, срез заштрихован */}
                <path d="M182 72 L286 72 L286 128 L182 128 Z"
                    fill="url(#dgHatch)" stroke="#23252b" strokeWidth="2"/>
                {/* Отверстие в разрезе — сплошными линиями, не штриховыми */}
                <rect x="214" y="72" width="28" height="56" fill="#ffffff"
                    stroke="#23252b" strokeWidth="2"/>
                <text x="234" y="152" className={cl.axHint} textAnchor="middle">
                    {labels.cut || 'отверстие стало видно'}
                </text>

                <text x="160" y="186" className={cl.axHint} textAnchor="middle">
                    {labels.note || 'разрез показывает то, что внутри'}
                </text>
            </svg>
        </div>
    );
}


/*
 * ── КУРС 3. «Детали, которые собираются» ──────────────────────────────
 */

/**
 * Зазор: почему деталь «точно по размеру» не влезает.
 *
 * Слева — вал и отверстие одного размера: не собирается. Справа — отверстие
 * чуть больше: заходит. Разница показана нарочно крупно, хотя в жизни это
 * доли миллиметра — иначе ребёнок её просто не увидит.
 */
function GapDiagram({ labels }) {
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                <defs>
                    <marker id="dgStop" markerWidth="8" markerHeight="8"
                        refX="4" refY="7" orient="auto">
                        <path d="M0 7 L8 7" stroke="#c0504d" strokeWidth="2.4"/>
                    </marker>
                </defs>

                {/* СЛЕВА: размеры совпадают — не лезет */}
                <rect x="22" y="62" width="110" height="66"
                    fill="#e8ecf2" stroke="#23252b" strokeWidth="2"/>
                <rect x="55" y="62" width="44" height="66"
                    fill="#ffffff" stroke="#23252b" strokeWidth="2"/>
                <rect x="55" y="28" width="44" height="30"
                    fill="#9aa5b5" stroke="#5b6472" strokeWidth="2"/>
                <path d="M77 24 L77 10" stroke="#c0504d" strokeWidth="2.4"
                    fill="none" markerEnd="url(#dgStop)"/>
                <text x="77" y="148" className={cl.axHint} textAnchor="middle">
                    {labels.tight || '20 и 20 — не входит'}
                </text>

                {/* СПРАВА: с зазором — заходит */}
                <rect x="188" y="62" width="110" height="66"
                    fill="#e8ecf2" stroke="#23252b" strokeWidth="2"/>
                <rect x="217" y="62" width="52" height="66"
                    fill="#ffffff" stroke="#23252b" strokeWidth="2"/>
                <rect x="221" y="62" width="44" height="50"
                    fill="#9aa5b5" stroke="#5b6472" strokeWidth="2"/>
                {/* Просветы по бокам — тот самый зазор */}
                <g stroke="#2f86d6" strokeWidth="1.8">
                    <path d="M217 74 L221 74"/>
                    <path d="M265 74 L269 74"/>
                </g>
                <text x="243" y="148" className={cl.axHint} textAnchor="middle">
                    {labels.loose || '20 и 20,4 — входит'}
                </text>

                <text x="160" y="180" className={cl.axHint} textAnchor="middle">
                    {labels.note || 'зазор — специально оставленный просвет'}
                </text>
            </svg>
        </div>
    );
}


/** Три посадки: свободная, плотная, с натягом. */
function FitsDiagram({ labels }) {
    /*
     * Ряды идут В ОДНУ ЛИНИЮ по горизонтали, подписи — строго под каждым.
     * Раньше подписи стояли сбоку и налезали на деталь: у .axHint в CSS
     * задан text-anchor: middle, и он перебивает textAnchor="start" из JSX.
     */
    const cols = [
        { x: 20, gap: 8, name: labels.free || 'свободная',
          hint: 'легко снимается' },
        { x: 116, gap: 4, name: labels.tight || 'плотная',
          hint: 'не болтается' },
        { x: 212, gap: 0, name: labels.press || 'с натягом',
          hint: 'обратно не снять' },
    ];
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                {cols.map((c) => (
                    <g key={c.name}>
                        {/* Отверстие */}
                        <rect x={c.x} y="58" width="88" height="44"
                            fill="#e8ecf2" stroke="#23252b" strokeWidth="1.8"/>
                        {/* Вал внутри — чем меньше зазор, тем плотнее сидит */}
                        <rect x={c.x + 18 + c.gap} y="64"
                            width={52 - c.gap * 2} height="32"
                            fill="#9aa5b5" stroke="#5b6472" strokeWidth="1.8"/>
                        {/* Просветы по бокам подсвечены — это и есть зазор */}
                        {c.gap > 0 && (
                            <g stroke="#2f86d6" strokeWidth="2">
                                <path d={`M${c.x + 18} 80 L${c.x + 18 + c.gap} 80`}/>
                                <path d={`M${c.x + 70 - c.gap} 80 L${c.x + 70} 80`}/>
                            </g>
                        )}
                        <text x={c.x + 44} y="124" className={cl.axHint}>
                            {c.name}
                        </text>
                        <text x={c.x + 44} y="141" className={cl.axHint}
                            opacity="0.72" style={{ fontSize: '11px' }}>
                            {c.hint}
                        </text>
                    </g>
                ))}
                <text x="160" y="176" className={cl.axHint}>
                    {labels.note || 'чем меньше зазор, тем плотнее посадка'}
                </text>
            </svg>
        </div>
    );
}


/**
 * Защёлка: пластик пружинит, крючок проскакивает и цепляется.
 * Три положения — подошла, отогнулась, защёлкнулась.
 */
function SnapDiagram({ labels }) {
    const step = (x, bend, label) => (
        <g key={label}>
            {/* Ответная стенка с выступом */}
            <rect x={x} y="54" width="15" height="74"
                fill="#e8ecf2" stroke="#23252b" strokeWidth="1.8"/>
            <rect x={x + 15} y="82" width="9" height="14"
                fill="#e8ecf2" stroke="#23252b" strokeWidth="1.8"/>
            {/* Язычок: отгибается на bend */}
            <path d={`M${x + 76} 54 L${x + 38 + bend} 68 L${x + 38 + bend} 112 `
                + `L${x + 76} 126 Z`}
                fill="#9aa5b5" stroke="#5b6472" strokeWidth="1.8"/>
            {/* Крючок на конце язычка */}
            <path d={`M${x + 38 + bend} 84 L${x + 27 + bend} 89 `
                + `L${x + 38 + bend} 96 Z`}
                fill="#8d97a7" stroke="#5b6472" strokeWidth="1.6"/>
            <text x={x + 44} y="152" className={cl.axHint} textAnchor="middle">
                {label}
            </text>
        </g>
    );
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                {step(6, 0, labels.a || 'подошла')}
                {step(112, 13, labels.b || 'отогнулась')}
                {step(218, 0, labels.c || 'защёлкнулась')}
                <text x="160" y="180" className={cl.axHint} textAnchor="middle">
                    {labels.note || 'пластик пружинит и возвращается на место'}
                </text>
            </svg>
        </div>
    );
}


/**
 * Что не напечатается: тонкая стенка и нависание.
 * Принтер кладёт пластик снизу вверх, и в воздух класть его не на что.
 */
function PrintableDiagram({ labels }) {
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                {/* СЛЕВА: слишком тонкая стенка */}
                <rect x="26" y="50" width="104" height="74"
                    fill="none" stroke="#23252b" strokeWidth="2"/>
                <rect x="30" y="54" width="96" height="66"
                    fill="none" stroke="#c0504d" strokeWidth="1.2"/>
                <text x="78" y="144" className={cl.axHint} textAnchor="middle"
                    style={{ fontSize: '11.5px' }}>
                    {labels.thin || 'стенка слишком тонкая'}
                </text>
                <text x="78" y="159" className={cl.axHint} textAnchor="middle"
                    opacity="0.72" style={{ fontSize: '11px' }}>
                    {labels.thinFix || 'нужно от 1,5 мм'}
                </text>

                {/* СПРАВА: нависание */}
                <line x1="176" y1="124" x2="302" y2="124"
                    stroke="#8a93a3" strokeWidth="2"/>
                <rect x="184" y="84" width="22" height="40"
                    fill="#9aa5b5" stroke="#5b6472" strokeWidth="1.8"/>
                <rect x="184" y="70" width="98" height="14"
                    fill="#9aa5b5" stroke="#5b6472" strokeWidth="1.8"/>
                {/* Пустота под полкой */}
                <g stroke="#c0504d" strokeWidth="1.3" strokeDasharray="4 3">
                    <path d="M214 84 L214 124"/>
                    <path d="M248 84 L248 124"/>
                    <path d="M280 84 L280 124"/>
                </g>
                <text x="240" y="144" className={cl.axHint} textAnchor="middle"
                    style={{ fontSize: '11.5px' }}>
                    {labels.over || 'полка висит в воздухе'}
                </text>
                <text x="240" y="159" className={cl.axHint} textAnchor="middle"
                    opacity="0.72" style={{ fontSize: '11px' }}>
                    {labels.overFix || 'нужен скос'}
                </text>

                <text x="160" y="184" className={cl.axHint} textAnchor="middle">
                    {labels.note || 'принтер кладёт пластик снизу вверх'}
                </text>
            </svg>
        </div>
    );
}


/*
 * ── КУРС 5. «Проектирование по заданию» ───────────────────────────────
 */

/**
 * Техзадание: требования превращаются в числа.
 *
 * Главная мысль урока — «удобно держать» это не требование, а пожелание.
 * Требование звучит как число, которое можно проверить линейкой.
 */
function BriefDiagram({ labels }) {
    const rows = [
        [labels.a || 'должно быть удобно', labels.a2 || 'ручка 25–30 мм'],
        [labels.b || 'чтобы не падало', labels.b2 || 'основание от 80 мм'],
        [labels.c || 'не слишком большое', labels.c2 || 'влезает в 150 мм'],
    ];

    /*
     * Рамки считаем ПОД ТЕКСТ, а не наоборот: строки приходят из урока,
     * и заранее неизвестно, длинные они или короткие. Высота строки растёт,
     * если текст не влез в одну.
     */
    const FS_L = 11.5, FS_R = 11;
    const W_L = 132, W_R = 138;
    const wrapped = rows.map(([a, b]) => [
        wrapText(a, W_L - 14, FS_L),
        wrapText(b, W_R - 16, FS_R),
    ]);
    const maxLines = Math.max(...wrapped.map(([la, lb]) =>
        Math.max(la.length, lb.length)));
    const rowH = Math.max(30, 14 + maxLines * FS_L * 1.3);
    const gap = 10;
    const totalH = rows.length * rowH + (rows.length - 1) * gap;
    const top = 100 - totalH / 2 + rowH / 2;

    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                <text x="76" y={top - rowH / 2 - 8} className={cl.axHint}
                    style={{ fontWeight: 700 }}>пожелание</text>
                <text x="242" y={top - rowH / 2 - 8} className={cl.axHint}
                    style={{ fontWeight: 700 }}>требование</text>

                {rows.map(([a, b], i) => {
                    const y = top + i * (rowH + gap);
                    const [la, lb] = wrapped[i];
                    return (
                        <g key={a}>
                            <rect x={10} y={y - rowH / 2} width={W_L} height={rowH}
                                rx="3" fill="#f7e9e6" stroke="#c0504d"
                                strokeWidth="1.4"/>
                            <MultiText x={10 + W_L / 2} y={y + FS_L * 0.35}
                                lines={la} fontSize={FS_L}
                                className={cl.axHint}
                                style={{ fontSize: FS_L + 'px' }}/>

                            <path d={`M${10 + W_L + 4} ${y} L${172} ${y}`}
                                stroke="#5a6474" strokeWidth="1.6" fill="none"
                                markerEnd="url(#dgArrow2)"/>

                            <rect x={172} y={y - rowH / 2} width={W_R} height={rowH}
                                rx="3" fill="#e6f0e9" stroke="#2e7d5b"
                                strokeWidth="1.4"/>
                            {/* Обычный шрифт, а не моноширинный dimTxt:
                                моноширинный жирный шире расчёта и вылезал
                                за рамку. Моноширинный нужен числам, где важны
                                разряды, а тут — обычная фраза. */}
                            <MultiText x={172 + W_R / 2} y={y + FS_R * 0.35}
                                lines={lb} fontSize={FS_R}
                                className={cl.axHint}
                                style={{ fontSize: FS_R + 'px', fill: '#2e7d5b',
                                    fontWeight: 700 }}/>
                        </g>
                    );
                })}

                <text x="160" y={top + (rows.length - 1) * (rowH + gap)
                    + rowH / 2 + 18} className={cl.axHint}>
                    {labels.note || 'требование можно проверить линейкой'}
                </text>
            </svg>
        </div>
    );
}


/** Измерение реального предмета: что мерить и где добавить запас. */
function MeasureDiagram({ labels }) {
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                {/* Предмет — телефон */}
                <rect x="106" y="40" width="72" height="118" rx="7"
                    fill="#8d97a7" stroke="#5b6472" strokeWidth="1.8"/>
                <rect x="112" y="48" width="60" height="96" rx="3"
                    fill="#b9c3d1" stroke="#5b6472" strokeWidth="1"/>

                {/* Размеры с трёх сторон — как измеряют линейкой */}
                <g stroke="#2f86d6" strokeWidth="1.5" fill="none">
                    <path d="M106 174 L178 174"/>
                    <path d="M106 170 L106 178"/>
                    <path d="M178 170 L178 178"/>
                    <path d="M92 40 L92 158"/>
                    <path d="M88 40 L96 40"/>
                    <path d="M88 158 L96 158"/>
                </g>
                <text x="142" y="190" className={cl.dimTxt}>
                    {labels.w || '72'}
                </text>
                <text x="74" y="102" className={cl.dimTxt}>
                    {labels.h || '148'}
                </text>

                {/* Запас на чехол — показан пунктиром вокруг */}
                <rect x="98" y="32" width="88" height="134" rx="9"
                    fill="none" stroke="#2e7d5b" strokeWidth="1.6"
                    strokeDasharray="6 4"/>
                <text x="248" y="70" className={cl.axHint}
                    style={{ fontSize: '11.5px' }}>{labels.gap || 'запас на чехол'}</text>
                <text x="248" y="88" className={cl.dimTxt}
                    style={{ fontSize: '12px', fill: '#2e7d5b' }}>+2 мм</text>

                <text x="160" y="18" className={cl.axHint}>
                    {labels.note || 'меряем предмет, потом добавляем запас'}
                </text>
            </svg>
        </div>
    );
}


/**
 * Облегчение: где можно убрать материал, а где нельзя.
 * Слева сплошная деталь, справа та же с рёбрами вместо тела.
 */
function LightenDiagram({ labels }) {
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                {/* Слева — сплошная */}
                <rect x="20" y="58" width="112" height="72"
                    fill="#8d97a7" stroke="#5b6472" strokeWidth="1.8"/>
                <text x="76" y="150" className={cl.axHint}
                    style={{ fontSize: '11.5px' }}>
                    {labels.solid || 'сплошная — тяжёлая'}
                </text>
                <text x="76" y="168" className={cl.dimTxt}
                    style={{ fontSize: '12px' }}>{labels.solidW || '84 г'}</text>

                {/* Справа — облегчённая рёбрами */}
                <rect x="188" y="58" width="112" height="72"
                    fill="none" stroke="#5b6472" strokeWidth="1.8"/>
                {/* Рёбра крест-накрест — материал убран между ними */}
                <g fill="#8d97a7" stroke="#5b6472" strokeWidth="1.2">
                    <rect x="188" y="58" width="112" height="9"/>
                    <rect x="188" y="121" width="112" height="9"/>
                    <rect x="188" y="58" width="9" height="72"/>
                    <rect x="291" y="58" width="9" height="72"/>
                    <rect x="239" y="58" width="9" height="72"/>
                    <rect x="188" y="90" width="112" height="9"/>
                </g>
                <text x="244" y="150" className={cl.axHint}
                    style={{ fontSize: '11.5px' }}>
                    {labels.ribbed || 'с рёбрами — лёгкая'}
                </text>
                <text x="244" y="168" className={cl.dimTxt}
                    style={{ fontSize: '12px', fill: '#2e7d5b' }}>
                    {labels.ribbedW || '31 г'}
                </text>

                <text x="160" y="30" className={cl.axHint}>
                    {labels.note || 'рёбра держат форму, а материала втрое меньше'}
                </text>
            </svg>
        </div>
    );
}


/** Разборная конструкция: сложное делят на печатаемые части. */
function SplitDiagram({ labels }) {
    return (
        <div className={cl.wrap}>
            <svg className={cl.svg} viewBox="0 0 320 200">
                {/* Слева — цельная деталь, которая не влезет на стол */}
                <rect x="16" y="52" width="120" height="88"
                    fill="#8d97a7" stroke="#5b6472" strokeWidth="1.8"/>
                {/* Граница стола принтера — деталь за неё вылезает */}
                <rect x="16" y="52" width="86" height="88"
                    fill="none" stroke="#c0504d" strokeWidth="1.6"
                    strokeDasharray="6 4"/>
                <text x="76" y="160" className={cl.axHint}
                    style={{ fontSize: '11.5px' }}>
                    {labels.whole || 'не влезает на стол'}
                </text>

                {/* Справа — та же деталь двумя частями со стыком */}
                <rect x="180" y="52" width="58" height="88"
                    fill="#8d97a7" stroke="#5b6472" strokeWidth="1.8"/>
                <rect x="246" y="52" width="58" height="88"
                    fill="#8d97a7" stroke="#5b6472" strokeWidth="1.8"/>
                {/* Шип и паз на стыке — детали не просто прикладывают */}
                <rect x="232" y="80" width="12" height="16"
                    fill="#b9c3d1" stroke="#2e7d5b" strokeWidth="1.4"/>
                <rect x="240" y="80" width="12" height="16"
                    fill="none" stroke="#2e7d5b" strokeWidth="1.4"/>
                <text x="242" y="160" className={cl.axHint}
                    style={{ fontSize: '11.5px' }}>
                    {labels.parts || 'две части со стыком'}
                </text>

                <text x="160" y="30" className={cl.axHint}>
                    {labels.note || 'делят там, где стык не мешает'}
                </text>
            </svg>
        </div>
    );
}
